import React, { useState, useEffect } from "react";
import { validateEducation } from "../../../utils/validation";
import "./styles/EducationForm.css";

const EducationForm = ({
  education = null,
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    institution: "",
    degree: "",
    // separate month/year for UI
    startYear: "",
    startMonth: "",
    endYear: "",
    endMonth: "",
    // ISO dates for backend
    startDate: "",
    endDate: "",
    isOngoing: false,
    rollNumber: "",
    grades: "",
    transcriptUrl: "",
    verifierEmail: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (education) {
      const startDate = education.startDate
        ? education.startDate.split("T")[0]
        : "";
      const endDate = education.endDate
        ? education.endDate.split("T")[0]
        : "";

      setFormData({
        institution: education.institution || "",
        degree: education.degree || "",
        startDate,
        endDate,
        startYear: startDate ? startDate.slice(0, 4) : "",
        startMonth: startDate ? startDate.slice(5, 7) : "",
        endYear: endDate ? endDate.slice(0, 4) : "",
        endMonth: endDate ? endDate.slice(5, 7) : "",
        isOngoing: education.isOngoing || false,
        rollNumber: education.rollNumber || "",
        grades: education.grades || "",
        transcriptUrl: education.transcriptUrl || "",
        verifierEmail: education.verifierEmail || "",
      });
    } else {
      setFormData({
        institution: "",
        degree: "",
        startYear: "",
        startMonth: "",
        endYear: "",
        endMonth: "",
        startDate: "",
        endDate: "",
        isOngoing: false,
        rollNumber: "",
        grades: "",
        transcriptUrl: "",
        verifierEmail: "",
      });
    }
    setErrors({});
    setTouched({});
  }, [education, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === "isOngoing" && newValue
        ? { endDate: "", endYear: "", endMonth: "" }
        : {}),
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // helpers for month/year selects, also update ISO dates
  const updateStart = (part, value) => {
    setFormData((prev) => {
      const startYear = part === "year" ? value : prev.startYear;
      const startMonth = part === "month" ? value : prev.startMonth;
      const startDate =
        startYear && startMonth ? `${startYear}-${startMonth}-01` : "";
      return { ...prev, startYear, startMonth, startDate };
    });
    if (errors.startDate) {
      setErrors((prev) => ({ ...prev, startDate: "" }));
    }
  };

  const updateEnd = (part, value) => {
    setFormData((prev) => {
      const endYear = part === "year" ? value : prev.endYear;
      const endMonth = part === "month" ? value : prev.endMonth;
      const endDate =
        endYear && endMonth ? `${endYear}-${endMonth}-01` : "";
      return { ...prev, endYear, endMonth, endDate };
    });
    if (errors.endDate) {
      setErrors((prev) => ({ ...prev, endDate: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldErrors = validateEducation(formData);
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] || "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    const validationErrors = validateEducation(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const submitData = {
        ...formData,
        startDate: formData.startDate || null,
        endDate: formData.isOngoing ? null : formData.endDate || null,
        rollNumber: formData.rollNumber || null,
        grades: formData.grades || null,
        transcriptUrl: formData.transcriptUrl || null,
      };
      onSubmit(submitData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edu-modal-overlay">
      <div className="edu-modal">
        <div className="edu-modal-header">
          <div>
            <h2 className="edu-modal-title">
              {education ? "Edit Education" : "Add Education"}
            </h2>
            <p className="edu-modal-subtitle">
              Enter your educational background details
            </p>
          </div>
          <button
            type="button"
            className="edu-modal-close"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form className="edu-form education-form" onSubmit={handleSubmit}>
          {/* Basic */}
          <div className="form-section">
            <h3>Basic Information</h3>

            <div className="form-group edu-form-group">
              <label htmlFor="institution">
                Institution Name <span className="required edu-required">*</span>
              </label>
              <input
                type="text"
                id="institution"
                name="institution"
                value={formData.institution}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`edu-input ${
                  errors.institution && touched.institution ? "error" : ""
                }`}
                placeholder="e.g., Harvard University"
                required
              />
              {errors.institution && touched.institution && (
                <span className="error-message edu-error">
                  {errors.institution}
                </span>
              )}
            </div>

            <div className="form-group edu-form-group">
              <label htmlFor="degree">
                Degree/Course <span className="required edu-required">*</span>
              </label>
              <input
                type="text"
                id="degree"
                name="degree"
                value={formData.degree}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`edu-input ${
                  errors.degree && touched.degree ? "error" : ""
                }`}
                placeholder="e.g., Bachelor of Computer Science"
                required
              />
              {errors.degree && touched.degree && (
                <span className="error-message edu-error">
                  {errors.degree}
                </span>
              )}
            </div>
          </div>

          {/* Academic */}
          <div className="form-section">
            <h3>Academic Details</h3>

            <div className="form-group edu-form-group">
              <label htmlFor="rollNumber">Student ID / Roll Number</label>
              <input
                type="text"
                id="rollNumber"
                name="rollNumber"
                value={formData.rollNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`edu-input ${
                  errors.rollNumber && touched.rollNumber ? "error" : ""
                }`}
                placeholder="e.g., CS2019123, 2019001"
              />
              {errors.rollNumber && touched.rollNumber && (
                <span className="error-message edu-error">
                  {errors.rollNumber}
                </span>
              )}
            </div>

            <div className="form-group edu-form-group">
              <label htmlFor="grades">GPA / Grades</label>
              <input
                type="text"
                id="grades"
                name="grades"
                value={formData.grades}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`edu-input ${
                  errors.grades && touched.grades ? "error" : ""
                }`}
                placeholder="e.g., 3.8, A Grade, 85%"
              />
              {errors.grades && touched.grades && (
                <span className="error-message edu-error">
                  {errors.grades}
                </span>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="form-section">
            <h3>Duration</h3>

            <div className="form-row edu-row">
              <div className="form-group edu-form-group">
                <label>
                  Start Month <span className="required edu-required">*</span>
                </label>
                <select
                  value={formData.startMonth}
                  onChange={(e) => updateStart("month", e.target.value)}
                  className="edu-input select-input"
                >
                  <option value="">Select month</option>
                  {[
                    "01",
                    "02",
                    "03",
                    "04",
                    "05",
                    "06",
                    "07",
                    "08",
                    "09",
                    "10",
                    "11",
                    "12",
                  ].map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, parseInt(m, 10) - 1, 1).toLocaleString(
                        "en-US",
                        { month: "long" }
                      )}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group edu-form-group">
                <label>
                  Start Year <span className="required edu-required">*</span>
                </label>
                <select
                  value={formData.startYear}
                  onChange={(e) => updateStart("year", e.target.value)}
                  className="edu-input select-input"
                >
                  <option value="">Select year</option>
                  {Array.from({ length: 50 }, (_, i) => 1980 + i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group checkbox-group edu-checkbox-row">
  <label className="checkbox-label edu-checkbox-label">
    <input
      type="checkbox"
      name="isOngoing"
      checked={formData.isOngoing}
      onChange={handleChange}
      className="edu-checkbox-input"
    />
    <span className="edu-checkbox-custom" />
  <span className="edu-checkbox-text">I currently attend this institution</span>
  </label>
</div>


            {!formData.isOngoing && (
              <div className="form-row edu-row">
                <div className="form-group edu-form-group">
                  <label>
                    End Month <span className="required edu-required">*</span>
                  </label>
                  <select
                    value={formData.endMonth}
                    onChange={(e) => updateEnd("month", e.target.value)}
                    className="edu-input select-input"
                  >
                    <option value="">Select month</option>
                    {[
                      "01",
                      "02",
                      "03",
                      "04",
                      "05",
                      "06",
                      "07",
                      "08",
                      "09",
                      "10",
                      "11",
                      "12",
                    ].map((m) => (
                      <option key={m} value={m}>
                        {new Date(
                          2000,
                          parseInt(m, 10) - 1,
                          1
                        ).toLocaleString("en-US", { month: "long" })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group edu-form-group">
                  <label>
                    End Year <span className="required edu-required">*</span>
                  </label>
                  <select
                    value={formData.endYear}
                    onChange={(e) => updateEnd("year", e.target.value)}
                    className="edu-input select-input"
                  >
                    <option value="">Select year</option>
                    {Array.from({ length: 50 }, (_, i) => 1980 + i).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Verification */}
          <div className="form-section">
            <h3>Verification Details</h3>

            <div className="form-group edu-form-group">
              <label htmlFor="verifierEmail">
                Institution Email for Verification{" "}
                <span className="required edu-required">*</span>
              </label>
              <input
                type="email"
                id="verifierEmail"
                name="verifierEmail"
                value={formData.verifierEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`edu-input ${
                  errors.verifierEmail && touched.verifierEmail ? "error" : ""
                }`}
                placeholder="registrar@university.edu, admin@college.com"
                required
              />
              {errors.verifierEmail && touched.verifierEmail && (
                <span className="error-message edu-error">
                  {errors.verifierEmail}
                </span>
              )}
              <small className="help-text">
                Official institutional email that can verify your education.
                This will be used to send verification requests.
              </small>
            </div>
          </div>

          {/* Actions */}
          <div className="edu-form-actions">
            <button
              type="button"
              className="edu-btn edu-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edu-btn edu-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner" />
                  {education ? "Updating..." : "Adding..."}
                </>
              ) : education ? (
                "Update Education"
              ) : (
                "Add Education"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EducationForm;
